import React, { useState, useMemo, useRef, useEffect } from 'react';
import Button from '../../components/Button/Button';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import Pagination from '../../components/Pagination/Pagination';
import { PetStatus, STATUS_LABELS, STATUS_COLORS } from '../../lib/utils';
import { trackPetChipMenuOpened, trackPetDeviceMenuOpened } from '../../analytics';
import styles from './Pets.module.scss';

interface DeviceMenuAction {
  label: string;
  key: string;
  dividerBefore?: boolean;
  danger?: boolean;
  fosterOnly?: boolean;
  rescueOnly?: boolean;
  hideWhenAdopted?: boolean;
}

const DEVICE_ACTIONS: DeviceMenuAction[] = [
  { label: 'View Device', key: 'view' },
  { label: 'Transfer to Adopter', key: 'transferAdopter', rescueOnly: true },
  { label: 'Transfer to Foster', key: 'transferFoster', rescueOnly: true },
  { label: 'Recall Device', key: 'recallDevice', fosterOnly: true },
  { label: 'Remove From Pet', key: 'removeFromPet', danger: true, dividerBefore: true, hideWhenAdopted: true },
];

function DeviceDropdown({
  device,
  onAction,
  petStatus,
}: {
  device: { id: string; moduleId: string };
  onAction: (key: string, moduleId: string) => void;
  petStatus?: PetStatus;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.deviceDropdown} ref={ref}>
      <button
        type="button"
        className={styles.deviceTag}
        onClick={() =>
          setOpen((prev) => {
            if (!prev) {
              trackPetDeviceMenuOpened();
            }
            return !prev;
          })
        }
      >
        <span className={styles.deviceTagIcon}>&#9673;</span>
        {device.moduleId}
        <span className={styles.deviceTagChevron}>&#9662;</span>
      </button>
      {open && (
        <ul className={styles.deviceMenu} role="menu">
          {DEVICE_ACTIONS.filter(
            (a) =>
              (!a.fosterOnly || petStatus === 'foster') &&
              (!a.rescueOnly || petStatus === 'rescue') &&
              (!a.hideWhenAdopted || petStatus !== 'adopted'),
          ).map((action) => (
            <React.Fragment key={action.key}>
              {action.dividerBefore && <li className={styles.deviceMenuDivider} role="separator" />}
              <li
                role="menuitem"
                tabIndex={0}
                className={`${styles.deviceMenuItem} ${action.danger ? styles.deviceMenuItemDanger : ''}`}
                onClick={() => {
                  onAction(action.key, device.moduleId);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onAction(action.key, device.moduleId);
                    setOpen(false);
                  }
                }}
              >
                {action.label}
              </li>
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
}

interface ChipMenuAction {
  label: string;
  key: string;
  dividerBefore?: boolean;
  danger?: boolean;
  ownerOnly?: boolean;
  noOwnerOnly?: boolean;
  /**
   * Hide for pets in pending_adoption or adopted status. Both Add Owner and
   * Clear Owner call backend mutations (`assignNewOwnerToChip` / `recallChip`)
   * that overwrite `pet.householdFk` as a side effect — for pending_adoption
   * this would yank the pet out of its "Adoption: {petName}" household and
   * strand the transfer; for adopted it would yank the pet out of the
   * adopter's household. The Chips page applies the same gate via
   * `adoptedPetIds` for cross-page consistency.
   */
  hideOnAdoptionFlow?: boolean;
}

const CHIP_ACTIONS: ChipMenuAction[] = [
  { label: 'View Chip Details', key: 'viewChip' },
  { label: 'Add Owner', key: 'addOwner', noOwnerOnly: true, hideOnAdoptionFlow: true },
  { label: 'Clear Owner', key: 'clearOwner', danger: true, ownerOnly: true, hideOnAdoptionFlow: true },
];

function ChipDropdown({
  chip,
  hasOwner,
  petStatus,
  onAction,
}: {
  chip: { id: string; serialNumber: string };
  hasOwner: boolean;
  petStatus?: PetStatus;
  onAction: (key: string, chipId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.deviceDropdown} ref={ref}>
      <button
        type="button"
        className={styles.chipTagBtn}
        onClick={() =>
          setOpen((prev) => {
            if (!prev) {
              trackPetChipMenuOpened();
            }
            return !prev;
          })
        }
      >
        <span className={styles.chipTagIcon}>&#9672;</span>
        {chip.serialNumber}
        <span className={styles.deviceTagChevron}>&#9662;</span>
      </button>
      {open && (
        <ul className={styles.deviceMenu} role="menu">
          {CHIP_ACTIONS.filter(
            (a) =>
              (!a.ownerOnly || hasOwner) &&
              (!a.noOwnerOnly || !hasOwner) &&
              (!a.hideOnAdoptionFlow || (petStatus !== 'pending_adoption' && petStatus !== 'adopted')),
          ).map((action) => (
            <React.Fragment key={action.key}>
              {action.dividerBefore && <li className={styles.deviceMenuDivider} role="separator" />}
              <li
                role="menuitem"
                tabIndex={0}
                className={`${styles.deviceMenuItem} ${action.danger ? styles.deviceMenuItemDanger : ''}`}
                onClick={() => {
                  onAction(action.key, chip.id);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onAction(action.key, chip.id);
                    setOpen(false);
                  }
                }}
              >
                {action.label}
              </li>
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChipCell({
  chip,
  petId,
  petStatus,
  onChipAction,
}: {
  chip: {
    id: string;
    shortId: string;
    serialNumber: string;
    latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } | null } | null;
  };
  petId: string;
  petStatus?: PetStatus;
  onChipAction?: (actionKey: string, chipId: string, shortId: string, serialNumber: string, petId: string) => void;
}) {
  return (
    <ChipDropdown
      chip={chip}
      hasOwner={!!chip.latestContactHistoryEntry?.contactInfo?.primaryEmail}
      petStatus={petStatus}
      onAction={(key, chipId) => onChipAction?.(key, chipId, chip.shortId, chip.serialNumber, petId)}
    />
  );
}

function timeAgo(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) {
    return 'just now';
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }
  const weeks = Math.floor(days / 7);
  if (weeks < 5) {
    return `${weeks}w ago`;
  }
  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months}mo ago`;
  }
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export interface PetRow {
  id: string;
  name: string;
  species?: string | null;
  updatedAt?: string | null;
  breed?: { name: string } | null;
  device?: { id: string; moduleId: string } | null;
  chip?: {
    id: string;
    shortId: string;
    serialNumber: string;
    latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } | null } | null;
  } | null;
  hasDevice: boolean;
  hasChip: boolean;
  status?: PetStatus;
}

interface PetsTableProps {
  pets: PetRow[];
  loading: boolean;
  onAssignDevice?: (petId: string) => void;
  onViewPet?: (pet: PetRow) => void;
  onCreatePet?: () => void;
  onDeviceAction?: (actionKey: string, moduleId: string, petId: string) => void;
  onChipAction?: (actionKey: string, chipId: string, shortId: string, serialNumber: string, petId: string) => void;
  onAddChip?: (pet: PetRow) => void;
}

export default function PetsTable({
  pets,
  loading,
  onAssignDevice,
  onViewPet,
  onCreatePet,
  onDeviceAction,
  onChipAction,
  onAddChip,
}: PetsTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [sortKey, setSortKey] = useState<'name' | 'chip' | 'device' | 'status' | null>('status');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  function handleSort(key: 'name' | 'chip' | 'device' | 'status') {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  }

  const sortedPets = useMemo(() => {
    if (!sortKey) {
      return pets;
    }
    const sorted = [...pets].sort((a, b) => {
      let aVal: string;
      let bVal: string;
      if (sortKey === 'name') {
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
      } else if (sortKey === 'chip') {
        aVal = a.chip?.shortId?.toLowerCase() ?? '';
        bVal = b.chip?.shortId?.toLowerCase() ?? '';
      } else if (sortKey === 'device') {
        aVal = a.device?.moduleId?.toLowerCase() ?? '';
        bVal = b.device?.moduleId?.toLowerCase() ?? '';
      } else {
        // Push pets without dates to the end regardless of sort direction
        const aDate = a.updatedAt ?? '';
        const bDate = b.updatedAt ?? '';
        if (!aDate && bDate) {
          return 1;
        }
        if (aDate && !bDate) {
          return -1;
        }
        aVal = aDate;
        bVal = bDate;
      }
      if (aVal < bVal) {
        return sortDir === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortDir === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [pets, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedPets.length / pageSize));
  const clampedPage = Math.min(currentPage, totalPages);

  const pagedPets = useMemo(() => {
    const page = Math.min(currentPage, Math.max(1, Math.ceil(sortedPets.length / pageSize)));
    const start = (page - 1) * pageSize;
    return sortedPets.slice(start, start + pageSize);
  }, [sortedPets, currentPage, pageSize]);

  function handlePageSizeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  }

  if (loading) {
    return null;
  }

  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionHeader} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 className={styles.sectionTitle}>Your Pets ({pets.length})</h2>
        </div>
        {onCreatePet && (
          <Button variant="green" size="sm" onClick={onCreatePet}>
            +Add Pet
          </Button>
        )}
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.sortableHeader} onClick={() => handleSort('name')}>
                Pet {sortKey === 'name' && <span className={styles.sortArrow}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
              </th>
              <th className={styles.sortableHeader} onClick={() => handleSort('status')}>
                Status{' '}
                {sortKey === 'status' && <span className={styles.sortArrow}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
              </th>
              <th className={styles.sortableHeader} onClick={() => handleSort('chip')}>
                Chip {sortKey === 'chip' && <span className={styles.sortArrow}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
              </th>
              <th className={styles.sortableHeader} onClick={() => handleSort('device')}>
                Device{' '}
                {sortKey === 'device' && <span className={styles.sortArrow}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
              </th>
              <th style={{ textAlign: 'right' }} />
            </tr>
          </thead>
          <tbody>
            {pagedPets.length === 0 && (
              <tr>
                <td colSpan={5} className={styles.emptyRow}>
                  No pets found
                </td>
              </tr>
            )}
            {pagedPets.map((pet) => (
              <tr key={pet.id}>
                <td>
                  <div style={{ fontWeight: 700 }}>{pet.name}</div>
                  <div className={styles.petBreed}>{pet.breed?.name ?? '\u2014'}</div>
                </td>
                <td>
                  <span className={styles.statusCell}>
                    <span className={styles[`statusBadge_${STATUS_COLORS[pet.status ?? 'rescue']}`]}>
                      {STATUS_LABELS[pet.status ?? 'rescue']}
                    </span>
                    {pet.updatedAt && (
                      <>
                        <span className={styles.statusDot}>&middot;</span>
                        <span className={styles.statusDate}>{timeAgo(pet.updatedAt)}</span>
                      </>
                    )}
                  </span>
                </td>
                <td>
                  {pet.hasChip && pet.chip && (
                    <ChipCell chip={pet.chip} petId={pet.id} petStatus={pet.status} onChipAction={onChipAction} />
                  )}
                  {!pet.hasChip && pet.status === 'adopted' && <span className={styles.emptyCell}>&mdash;</span>}
                  {!pet.hasChip && onAddChip && pet.status !== 'adopted' && (
                    <button type="button" className={styles.addDeviceBtn} onClick={() => onAddChip(pet)}>
                      +Add
                    </button>
                  )}
                </td>
                <td>
                  <div className={styles.setupPills}>
                    {pet.hasDevice && pet.device && (
                      <DeviceDropdown
                        device={pet.device}
                        onAction={(key, moduleId) => onDeviceAction?.(key, moduleId, pet.id)}
                        petStatus={pet.status}
                      />
                    )}
                    {!pet.hasDevice && pet.status === 'adopted' && <span className={styles.emptyCell}>&mdash;</span>}
                    {!pet.hasDevice && onAssignDevice && pet.status !== 'adopted' && (
                      <button type="button" className={styles.addDeviceBtn} onClick={() => onAssignDevice(pet.id)}>
                        +Add
                      </button>
                    )}
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <div className={styles.rowActions} style={{ justifyContent: 'flex-end' }}>
                    {onViewPet && (
                      <button type="button" className={styles.viewBtn} onClick={() => onViewPet(pet)}>
                        View
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.paginationWrapper}>
        <div className={styles.pageSizeRow}>
          <label className={styles.pageSizeLabel}>
            Rows per page
            <PortalSelect value={pageSize} onChange={handlePageSizeChange} className={styles.pageSizeSelect}>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </PortalSelect>
          </label>
        </div>
        <Pagination
          currentPage={clampedPage}
          totalPages={totalPages}
          totalItems={pets.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
